import i18n from '@common/i18n';
import { DateTime } from 'luxon';
import { dateToStringFormat, parseDateString } from '@common/utils/date';
import { generateColor } from '@common/utils/mission-color';
import { PieSection } from 'components/data-widgets/CustomPieChart';
import { BucketSize, FieldType, FilterType, FilterOperation } from 'models/enums/fleet-management';
import { FleetDataFilter } from 'models/fleet-management/FleetDataFilter';
import FleetWidget, { WidgetField } from 'models/fleet-management/FleetWidget';

const getValue = (obj: any, path: string): any => {
  // supports nested fields like "vehicle.engine.power"
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

const toStr = (val: any): string => {
  return val == null ? '' : String(val);
};

const getDatePart = (value: any, part: 'year' | 'month' | 'day'): number | null => {
  if (!value) return null;

  const d = new Date(value);
  if (isNaN(d.getTime())) return null;

  switch (part) {
    case 'year':
      return d.getFullYear();
    case 'month':
      return d.getMonth() + 1; // JS months are 0-based
    case 'day':
      return d.getDate();
  }
};

const applyFilter = (item: any, filter: FleetDataFilter) => {
  const value = getValue(item, filter.field);

  const [v1, v2] = filter.values;

  switch (filter.type) {
    case FilterType.EXACT:
      return value === v1;

    case FilterType.I_EXACT:
      return toStr(value) === toStr(v1);

    case FilterType.CONTAINS:
      return toStr(value)?.includes(String(v1));

    case FilterType.I_CONTAINS:
      return toStr(value)?.toLowerCase().includes(toStr(v1).toLowerCase());

    case FilterType.STARTS_WITH:
      return toStr(value)?.startsWith(String(v1));

    case FilterType.ENDS_WIDTH:
      return toStr(value)?.endsWith(String(v1));

    case FilterType.REGEX:
      return new RegExp(String(v1)).test(toStr(value));

    case FilterType.I_REGEX:
      return new RegExp(String(v1), 'i').test(toStr(value));

    case FilterType.IN:
      return Array.isArray(value)
        ? value.some((v) => filter.values.includes(v))
        : filter.values.includes(value);

    case FilterType.IS_NULL:
      return (
        (v1 === 'true' && (value === null || value === undefined)) ||
        (v1 !== 'true' && value !== null && value !== undefined)
      );

    case FilterType.GT:
      return Number(value) > Number(v1);

    case FilterType.GTE:
      return Number(value) >= Number(v1);

    case FilterType.LT:
      return Number(value) < Number(v1);

    case FilterType.LTE:
      return Number(value) <= Number(v1);

    case FilterType.RANGE:
      return Number(value) >= Number(v1) && Number(value) <= Number(v2);

    case FilterType.YEAR:
      return getDatePart(value, 'year') === Number(v1);

    case FilterType.MONTH:
      return getDatePart(value, 'month') === Number(v1);

    case FilterType.DAY:
      return getDatePart(value, 'day') === Number(v1);

    default:
      return true;
  }
};

export const filterData = (data: any[], filters: FleetDataFilter[]) => {
  if (filters.length === 0) return data;

  return data.filter((item) => filters.every((filter) => applyFilter(item, filter)));
};

export const getFlatFieldValues = (data: any[], field: string): any[] => {
  if (!Array.isArray(data)) return [];

  return data.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];

    const value = item[field];

    if (value == null) return [];

    // If it's already an array → flatten it
    if (Array.isArray(value)) {
      return value.filter((v) => v != null);
    }

    // Otherwise wrap single value
    return [value];
  });
};

export const getDataValueByFieldAndOperation = (field: WidgetField, data: any[] | null) => {
  if (data == null || field.operation == null) return null;

  const values = data.map((item) => item?.[field.name]).filter((v) => v != null);

  if (values.length === 0) return null;

  const isNumber = (v: any): v is number => typeof v === 'number' && !isNaN(v);
  const isString = (v: any): v is string => typeof v === 'string';
  const isIsoDate = (v: any): boolean => isString(v) && !isNaN(Date.parse(v));
  const isList = (v: any): v is (string | number)[] => Array.isArray(v);

  const flattenListsIfNeeded = () => {
    // if ANY value is a list, ALL must be lists
    if (!values.some(isList)) return values;

    if (!values.every(isList)) return { error: 'Mixed list and non-list values.' };

    return (values as (string | number)[][]).flat();
  };

  // Try to flatten data if operation allows lists
  const flattened = flattenListsIfNeeded();
  if ('error' in (flattened as any)) return (flattened as any).error;

  const valArr = Array.isArray(flattened) ? flattened : values;

  // Reusable numeric extractor
  const numericValues = valArr.filter(isNumber) as number[];

  switch (field.operation) {
    case FilterOperation.COUNT:
      return valArr.length.toString();

    case FilterOperation.FIRST:
      return valArr[0]?.toString() ?? null;

    case FilterOperation.LAST:
      return valArr[valArr.length - 1]?.toString() ?? null;

    case FilterOperation.SUM:
      if (numericValues.length !== valArr.length) return 'SUM only works on numeric values';
      return numericValues.reduce((a, b) => a + b, 0);

    case FilterOperation.AVG: {
      if (numericValues.length !== valArr.length) return 'AVG only works on numeric values';
      const sum = numericValues.reduce((a, b) => a + b, 0);
      return sum / numericValues.length;
    }

    case FilterOperation.MAX:
      // numeric max
      if (numericValues.length === valArr.length) {
        return Math.max(...numericValues).toString();
      }

      // date max
      if (valArr.every(isIsoDate)) {
        const max = valArr.map((v) => new Date(v)).sort((a, b) => b.getTime() - a.getTime())[0];
        return dateToStringFormat(max, true);
      }

      // string max (lexicographical)
      if (valArr.every(isString)) {
        return valArr.sort().pop()!;
      }

      return 'MAX cannot be applied to mixed or unsupported types';

    case FilterOperation.MIN:
      // numeric min
      if (numericValues.length === valArr.length) {
        return Math.min(...numericValues).toString();
      }

      // date min
      if (valArr.every(isIsoDate)) {
        const min = valArr.map((v) => new Date(v)).sort((a, b) => a.getTime() - b.getTime())[0];
        return dateToStringFormat(min, true);
      }

      // string min
      if (valArr.every(isString)) {
        return valArr.sort()[0];
      }

      return 'MIN cannot be applied to mixed or unsupported types';
    default:
      return null;
  }
};

export type FleetFieldTypeLookup = (fieldName: string) => FieldType | undefined;

export interface GroupPart {
  field: string;
  value: unknown;
  label: string;
}

export interface GroupEntry {
  key: string;
  parts: GroupPart[];
  items: any[];
}

export const MAX_CHART_GROUPS = 2000;
export const MAX_ROW_COMBINATIONS = 100;

const dateMillis = (value: unknown): number | null => {
  const parsed = DateTime.fromISO(String(value));
  return parsed.isValid ? parsed.toMillis() : null;
};

const compareValues = (a: unknown, b: unknown, type: FieldType): number => {
  if (type === FieldType.DATE) {
    const left = dateMillis(a);
    const right = dateMillis(b);
    if (left === right) return 0;
    if (left == null) return 1;
    if (right == null) return -1;
    return left - right;
  }

  if (type === FieldType.NUMBER) {
    const left = Number(a);
    const right = Number(b);
    const leftValid = !isNaN(left);
    const rightValid = !isNaN(right);
    if (!leftValid && !rightValid) return 0;
    if (!leftValid) return 1;
    if (!rightValid) return -1;
    return left - right;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

export const formatGroupValue = (value: unknown, type: FieldType | undefined): string => {
  if (value == null) return '';

  if (type === FieldType.DATE) {
    const parsed = DateTime.fromISO(String(value));
    if (!parsed.isValid) return String(value);

    const format = String(value).includes('T') ? DateTime.DATETIME_SHORT : DateTime.DATE_MED;
    return parsed.setLocale(i18n.language).toLocaleString(format);
  }

  if (type === FieldType.BOOL || typeof value === 'boolean') {
    return i18n.t(value ? 'label-true' : 'label-false');
  }

  return String(value);
};

const isNumericString = (candidate: string): boolean => {
  const trimmed = candidate.trim();
  if (trimmed === '') return false;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) && String(numeric) === trimmed;
};

export const formatWidgetValue = (value: unknown): string => {
  if (value == null) return '-';

  const isNumeric =
    typeof value === 'number' || (typeof value === 'string' && isNumericString(value));

  if (isNumeric) {
    const numericValue = Number(value);
    return isNaN(numericValue) ? '-' : new Intl.NumberFormat(i18n.language).format(numericValue);
  }

  return formatGroupValue(value, undefined);
};

const candidateValues = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value.filter((entry) => entry != null);
  return value == null ? [] : [value];
};

const crossProduct = (lists: unknown[][]): unknown[][] =>
  lists.reduce<unknown[][]>(
    (combinations, values) =>
      combinations.flatMap((prefix) => values.map((value) => [...prefix, value])),
    [[]],
  );

export const buildGroups = (
  groupFields: string[],
  data: any[],
  fieldType?: FleetFieldTypeLookup,
): GroupEntry[] => {
  if (!Array.isArray(data) || groupFields.length === 0) return [];

  const types = groupFields.map((field) => fieldType?.(field) ?? FieldType.STRING);
  const groups = new Map<string, GroupEntry>();

  for (const item of data) {
    const perField = groupFields.map((field) => candidateValues(getValue(item, field)));

    // A row with nothing to say about one of the group fields is not plotted.
    if (perField.some((values) => values.length === 0)) continue;

    // Checked before the cross product is built, not after: several list
    // group fields with many values each can multiply into an enormous
    // combination count for a single row, and MAX_CHART_GROUPS can only
    // refuse a combination once it already exists.
    const combinationCount = perField.reduce((total, values) => total * values.length, 1);
    if (combinationCount > MAX_ROW_COMBINATIONS) continue;

    for (const combination of crossProduct(perField)) {
      const key = JSON.stringify(combination);
      let entry = groups.get(key);

      if (entry == null) {
        if (groups.size >= MAX_CHART_GROUPS) continue;

        entry = {
          key,
          parts: combination.map((value, index) => ({
            field: groupFields[index],
            value,
            label: formatGroupValue(value, types[index]),
          })),
          items: [],
        };
        groups.set(key, entry);
      }

      entry.items.push(item);
    }
  }

  return Array.from(groups.values()).sort((left, right) => {
    for (let index = 0; index < types.length; index++) {
      const result = compareValues(left.parts[index].value, right.parts[index].value, types[index]);
      if (result !== 0) return result;
    }

    // Stable output for groups that compare equal on every part.
    return left.key.localeCompare(right.key);
  });
};

export interface ChartGroupMeta {
  parts: GroupPart[];
  rowCount: number;
}

export interface ChartAxisData {
  groups: string[];
  datasets: { label: string; data: any[] }[];
  groupMeta: ChartGroupMeta[];
}

const seriesLabel = (field: WidgetField) =>
  `${i18n.t('fleet-management-field-' + field.name)}${field.operation ? ` (${field.operation})` : ''}`;

const groupAxisLabel = (entry: GroupEntry) => entry.parts.map((part) => part.label).join(' | ');

export const getPieWidgetData = (
  widget: FleetWidget,
  data: any[] | null,
  fieldType?: FleetFieldTypeLookup,
) => {
  const pieSections: PieSection[] = [];

  if (
    data == null ||
    widget.groups == null ||
    widget.groups.length < 1 ||
    widget.fields == null ||
    widget.fields.length !== 1
  )
    return pieSections;

  const field = widget.fields[0];
  const entries = buildGroups(widget.groups, data, fieldType);

  entries.forEach((entry, index) => {
    pieSections.push({
      id: entry.key,
      label: groupAxisLabel(entry),
      value: getDataValueByFieldAndOperation(field, entry.items),
      color: generateColor('#118FF3', index, entries.length),
      parts: entry.parts,
      rowCount: entry.items.length,
    });
  });

  return pieSections.sort((a, b) => b.value - a.value);
};

export const getWidgetChartAxisData = (
  widget: FleetWidget,
  data: any[] | null,
  fieldType?: FleetFieldTypeLookup,
): ChartAxisData => {
  const emptyResponse: ChartAxisData = { groups: [], datasets: [], groupMeta: [] };

  if (
    data == null ||
    widget.fields == null ||
    widget.fields.length < 1 ||
    widget.groups == null ||
    widget.groups.length < 1
  )
    return emptyResponse;

  const { groups: groupFields, fields } = widget;
  const entries = buildGroups(groupFields, data, fieldType);
  if (entries.length === 0) return emptyResponse;

  const datasets = fields.map((field) => ({ label: seriesLabel(field), data: [] as any[] }));
  const groups: string[] = [];
  const groupMeta: ChartGroupMeta[] = [];

  for (const entry of entries) {
    groups.push(groupAxisLabel(entry));
    groupMeta.push({ parts: entry.parts, rowCount: entry.items.length });

    fields.forEach((field, index) => {
      datasets[index].data.push(getDataValueByFieldAndOperation(field, entry.items));
    });
  }

  return { groups, datasets, groupMeta };
};

/** A filter day (`YYYY-MM-DD`) as a short localised date, for labels and captions. */
export const formatFilterDay = (day: string, language: string) =>
  parseDateString(day).setLocale(language).toLocaleString(DateTime.DATE_MED);

/** The selected window as one readable label, so a caption can say what a number covers. */
export const formatFilterPeriod = (start: string, end: string, language: string) =>
  `${formatFilterDay(start, language)} – ${formatFilterDay(end, language)}`;

const BUCKET_UNIT: Record<BucketSize, 'week' | 'month' | 'year'> = {
  [BucketSize.WEEK]: 'week',
  [BucketSize.MONTH]: 'month',
  [BucketSize.YEAR]: 'year',
};

const MAX_BUCKETS = 600;

export const CUMULATIVE_OPERATIONS = [FilterOperation.COUNT, FilterOperation.SUM];

export const isCumulativeField = (field?: WidgetField | null) =>
  field?.operation != null && CUMULATIVE_OPERATIONS.includes(field.operation);

const BUCKET_LABEL_FORMAT: Record<BucketSize, string> = {
  [BucketSize.WEEK]: 'dd LLL yyyy',
  [BucketSize.MONTH]: 'LLL yyyy',
  [BucketSize.YEAR]: 'yyyy',
};

const formatBucketLabel = (bucketStart: DateTime, bucket: BucketSize) =>
  bucketStart.setLocale(i18n.language).toFormat(BUCKET_LABEL_FORMAT[bucket]);

export const getCumulativeChartData = (widget: FleetWidget, data: any[] | null) => {
  const empty = {
    groups: [] as string[],
    datasets: [] as { label: string; data: number[] }[],
    groupMeta: [] as ChartGroupMeta[],
  };

  const { bucketField, bucket, fields } = widget;

  if (
    data == null ||
    bucketField == null ||
    bucket == null ||
    fields == null ||
    fields.length < 1 ||
    !isCumulativeField(fields[0])
  ) {
    return empty;
  }

  const unit = BUCKET_UNIT[bucket];
  const field = fields[0];

  const rows = data
    .map((item) => ({ item, at: DateTime.fromISO(String(item?.[bucketField])) }))
    .filter((row) => row.at.isValid)
    .map((row) => ({ item: row.item, bucket: row.at.startOf(unit) }))
    .sort((a, b) => a.bucket.valueOf() - b.bucket.valueOf());

  if (rows.length === 0) return empty;

  const buckets: DateTime[] = [];
  const last = DateTime.now().startOf(unit);
  const earliest = last.minus({ [unit]: MAX_BUCKETS - 1 });
  let cursor = rows[0].bucket > earliest ? rows[0].bucket : earliest;
  for (; cursor <= last; cursor = cursor.plus({ [unit]: 1 })) {
    buckets.push(cursor);
  }

  // Accumulate across every bucket first: a bucket dropped by `limit` still
  // contributes its rows to the buckets that survive.
  const groups: string[] = [];
  const meta: ChartGroupMeta[] = [];
  const values: number[] = [];
  const accumulated: any[] = [];
  let nextRow = 0;

  for (const bucketStart of buckets) {
    while (nextRow < rows.length && rows[nextRow].bucket <= bucketStart) {
      accumulated.push(rows[nextRow].item);
      nextRow++;
    }

    const iso = bucketStart.toISODate() ?? '';
    groups.push(formatBucketLabel(bucketStart, bucket));
    meta.push({
      parts: [{ field: bucketField, value: iso, label: formatBucketLabel(bucketStart, bucket) }],
      // Unlike the other two producers of ChartGroupMeta (buildGroups' entries,
      // read via groupMeta above), rowCount here is the cumulative total of
      // rows through this bucket, not the count of rows inside it alone.
      rowCount: accumulated.length,
    });
    values.push(Number(getDataValueByFieldAndOperation(field, accumulated)));
  }

  const keep = widget.limit != null && widget.limit > 0 ? widget.limit : buckets.length;

  return {
    groups: groups.slice(-keep),
    datasets: [
      {
        label: seriesLabel(field),
        data: values.slice(-keep),
      },
    ],
    groupMeta: meta.slice(-keep),
  };
};
